// Departments Master — manage the hr_departments list for the active company.
// Dedicated screen (reached from the Masters section of the sidebar). Deliberately NOT a tab
// inside Masters.js, which is mid-edit by another workstream.
// The same list feeds the attendance-grid filter and the employee form dropdown, so a row added
// here appears there immediately. Deactivate never deletes — existing employees keep their dept.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { HRC, Card, SectionTitle, Table, Th, Td, Btn, Field, input, Empty, Loading } from './hrkit';

export default function DepartmentsMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ name: '', code: '', sort_order: '' });
  const [editing, setEditing] = useState(null); // { id, name, code, sort_order }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/hr/departments', { params: { all: 1 } })
      .then((r) => setRows(r.data.data || []))
      .catch((e) => setErr(e.response?.data?.error || 'Failed to load departments'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const norm = (s) => (s || '').trim().toLowerCase();
  const activeDupe = (name, exceptId) =>
    rows.some((r) => r.is_active && r.id !== exceptId && norm(r.name) === norm(name));

  const add = async () => {
    const name = form.name.trim();
    if (!name) { setErr('Name is required'); return; }
    if (activeDupe(name)) { setErr(`A department named "${name}" already exists`); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/hr/departments', {
        name, code: form.code.trim() || null,
        sort_order: form.sort_order === '' ? undefined : Number(form.sort_order),
      });
      setForm({ name: '', code: '', sort_order: '' });
      load();
    } catch (e) { setErr(e.response?.data?.error || 'Create failed'); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    const name = (editing.name || '').trim();
    if (!name) { setErr('Name is required'); return; }
    if (activeDupe(name, editing.id)) { setErr(`A department named "${name}" already exists`); return; }
    setBusy(true); setErr('');
    try {
      await api.patch('/hr/departments/' + editing.id, {
        name, code: (editing.code || '').trim() || null,
        sort_order: editing.sort_order === '' ? 0 : Number(editing.sort_order),
      });
      setEditing(null);
      load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };

  const toggleActive = async (r) => {
    setBusy(true); setErr('');
    try { await api.patch('/hr/departments/' + r.id, { is_active: !r.is_active }); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Update failed'); } finally { setBusy(false); }
  };

  if (loading) return <Loading />;

  return (
    <div style={{ padding: 20, maxWidth: 920, margin: '0 auto' }}>
      <Card>
        <SectionTitle>Departments</SectionTitle>
        <p style={{ fontSize: 12, color: HRC.muted, marginTop: -4 }}>
          Master list for the active company. Feeds the attendance grid and employee form, and is read by
          payroll, appraisals and reports. Deactivate rather than delete — existing employees keep their department.
        </p>
        {err && (
          <div style={{ background: '#FCEBEB', color: '#B4232A', padding: '8px 12px', borderRadius: 6, fontSize: 13, margin: '10px 0' }}>{err}</div>
        )}

        {/* Add a department */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', margin: '12px 0 18px' }}>
          <div style={{ flex: '1 1 220px' }}>
            <Field label="New department"><input style={input} value={form.name} placeholder="e.g. Packing" onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          </div>
          <div style={{ width: 120 }}>
            <Field label="Code (optional)"><input style={input} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
          </div>
          <div style={{ width: 110 }}>
            <Field label="Sort order"><input style={input} type="number" value={form.sort_order} placeholder="auto" onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></Field>
          </div>
          <Btn onClick={add} disabled={busy}>+ Add</Btn>
        </div>

        <Table>
          <thead>
            <tr><Th>Order</Th><Th>Name</Th><Th>Code</Th><Th>Employees</Th><Th>Status</Th><Th style={{ textAlign: 'right' }}>Actions</Th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6}><Empty>No departments yet — add the first one above.</Empty></td></tr>
            ) : rows.map((r) => editing && editing.id === r.id ? (
              <tr key={r.id} style={{ background: '#FBFAF7' }}>
                <Td><input style={{ ...input, width: 70 }} type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} /></Td>
                <Td><input style={input} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Td>
                <Td><input style={{ ...input, width: 90 }} value={editing.code || ''} onChange={(e) => setEditing({ ...editing, code: e.target.value })} /></Td>
                <Td>{r.headcount}</Td>
                <Td>{r.is_active ? 'Active' : 'Inactive'}</Td>
                <Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Btn size="sm" onClick={saveEdit} disabled={busy}>Save</Btn>{' '}
                  <Btn size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
                </Td>
              </tr>
            ) : (
              <tr key={r.id} style={{ opacity: r.is_active ? 1 : 0.55 }}>
                <Td>{r.sort_order}</Td>
                <Td style={{ fontWeight: 600 }}>{r.name}</Td>
                <Td>{r.code || '—'}</Td>
                <Td>{r.headcount}</Td>
                <Td>{r.is_active
                  ? <span style={{ color: HRC.green, fontWeight: 600 }}>Active</span>
                  : <span style={{ color: '#999' }}>Inactive</span>}</Td>
                <Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Btn size="sm" variant="ghost" onClick={() => setEditing({ id: r.id, name: r.name, code: r.code, sort_order: r.sort_order })}>Edit</Btn>{' '}
                  <Btn size="sm" variant="ghost" onClick={() => toggleActive(r)} disabled={busy}>{r.is_active ? 'Deactivate' : 'Activate'}</Btn>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}
